// @ts-strict-ignore
import type { FetchRequestError } from "@lib/errors";
import { queryClient } from "@lib/queryClient";

import type { AllRouteEndpoints } from "../routeTypes";
import type { MutationFetcherFunctionWithShadowError } from "../types";
import { getInvalidateCacheKeys } from "./getInvalidationCacheKeys";

/**
 * Adapter to trigger invalidation of existing queries upon successful execution of a mutation
 */
export const reactQueryMutationAdapter = <Arguments, Result, Error extends FetchRequestError>(
  invalidationQueryKeys: AllRouteEndpoints | AllRouteEndpoints[],
  fetcherFunction: MutationFetcherFunctionWithShadowError<Error, Arguments, Result>
): MutationFetcherFunctionWithShadowError<Error, Arguments, Result> => {
  const cacheKeysToInvalidate = getInvalidateCacheKeys(invalidationQueryKeys);

  const result = (args: Arguments, customizedFetcherFunction?: Function, directCallFetcher = false) => {
    // If directCallFetcher is true, we are calling the fetcher directly with passed in args
    if (directCallFetcher) {
      return fetcherFunction(args);
    }

    // If customizedFetcher is provided, we use that instead of the default fetcherFunction
    const activeFetcherFunction = customizedFetcherFunction || fetcherFunction;

    return activeFetcherFunction(args).then((result) => {
      // each cache key needs to be invalidated independently. If we pass in the array of query keys, it will think they are 1 query key
      cacheKeysToInvalidate.forEach((key) => queryClient.invalidateQueries([key]));
      return result;
    });
  };
  return result as MutationFetcherFunctionWithShadowError<Error, Arguments, Result>;
};
